import { z } from 'zod/v3';
import { err, ok } from 'neverthrow';
import { toast } from '@cotera/client/app/components/ui';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '@cotera/client/v0/actions/framework';
import { DatasetModel } from './dataset.model';

const flushWarehouseParamsSchema = z.object({
  datasetId: z.string().optional(),
});

type Params = z.infer<typeof flushWarehouseParamsSchema>;
type Errors = { t: string; message?: string }[];

type FlushWarehouseActionClient = {
  resource: {
    dataset: Pick<TenantedClient['resource']['dataset'], 'flushWarehouse'>;
  };
};

/**
 * Pushes a warehouse dataset's pending operations into the org warehouse now.
 *
 * The op log is mirrored to the warehouse on the compaction debounce; this
 * forces that through immediately, running under the same lock so it cannot
 * race a scheduled compaction. A no-op when nothing is pending — everything is
 * already mirrored.
 */
export class FlushWarehouseAction extends BaseAction<Params, Errors> {
  title = 'Flush to warehouse';
  description =
    'Flush and sync pending changes into the connected warehouse now';
  inputSchema = flushWarehouseParamsSchema;
  group = 'Dataset';
  icon = 'circle-stack';

  constructor(private readonly client: FlushWarehouseActionClient) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return this.getTargetDatasetId(context) !== null;
  }

  async execute(
    payload: Params,
    context: ExecuteContext
  ): Promise<ActionResult<Errors>> {
    const datasetId = payload.datasetId ?? this.getTargetDatasetId(context);
    if (datasetId === null || datasetId === undefined) {
      return err([{ t: 'error', message: 'No dataset in scope' }]);
    }

    const result = await this.client.resource.dataset.flushWarehouse({
      id: datasetId,
    });
    if (result.isErr()) {
      const message =
        result.error.err.message ?? 'Failed to sync to warehouse.';
      toast.error(message);
      return err([{ t: 'error', message }]);
    }
    toast.success(
      result.value.flushed
        ? 'Syncing pending changes to the warehouse…'
        : 'Everything is already synced to the warehouse.'
    );
    return ok({});
  }

  private getTargetDatasetId(context: ApplicableContext): string | null {
    const datasetModel = context.targetedModelOfType(DatasetModel);
    return datasetModel?.datasetId ?? null;
  }
}
